import * as React from 'react';

import { useEffect, useMemo } from 'react';

import { getPlatformTableRowByIdTeamQueryKey } from '@cloudquery/api-platform';
import { FeatureFlag, useFeatureFlags } from '@cloudquery/base-users';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import { useTheme } from '@mui/material/styles';
import Tooltip from '@mui/material/Tooltip';
import {
  DataGrid,
  GridColumnVisibilityModel,
  GridNoRowsOverlay,
  GridRenderCellParams,
  GridValidRowModel,
  useGridApiRef,
  gridClasses,
  GridRowSelectionModel,
} from '@mui/x-data-grid';
import { useQueryClient } from '@tanstack/react-query';

import { CLOUD_ASSET_TABLE_NAME, EmptyBox, useDataGridClick } from 'shared/base';

import { CLOUD_ASSET_UNIQUE_KEY, ROW_EDIT_ID_FIELD } from '../../constants/data';
import { useCustomColumnEdit } from '../../hooks/useCustomColumnEdit';
import { useTableStateContext } from '../../hooks/useTableStateContext';
import { CustomColumnsMultiEdit } from '../controls/customColumnsMultiEdit';
import { CustomToolbar } from '../controls/customToolbar';
import { NotInitialized } from '../display/notInitialized';
import { AssetInventoryDetailView } from '../views/tableDetailView';

interface Props {
  enableWriteActions: boolean;
  onSaveCurrentFilter: () => void;
  teamName: string;
}

const slotProps = {
  columnHeaders: {
    style: {
      textTransform: 'uppercase' as const,
    },
  },
  loadingOverlay: {
    noRowsVariant: 'skeleton' as const,
    variant: 'skeleton' as const,
  },
};

export function AssetInventoryTable({ enableWriteActions, onSaveCurrentFilter, teamName }: Props) {
  const theme = useTheme();
  const { featureFlags } = useFeatureFlags();
  const { editing: allowEditingCustomColumns } = featureFlags[FeatureFlag.ALLOWED_CUSTOM_COLUMNS];
  const resizeTimer = React.useRef<number>(undefined);
  const {
    columns: tableColumns,
    detailIds,
    displayColumns,
    error,
    filter,
    hasEditableCustomColumns,
    isLoading: dataLoading,
    overwriteDisplayColumns,
    overwriteFilter,
    paginationModel,
    rowCount,
    rows: tableRows,
    setDetailIds,
    setPagination,
    setSort,
    sort,
  } = useTableStateContext();
  const { editCustomColumnValues } = useCustomColumnEdit();
  const [lastRows, setLastRows] = React.useState<GridValidRowModel[]>([]);
  const [selectedRows, setSelectedRows] = React.useState<GridRowSelectionModel>({
    ids: new Set(),
    type: 'include',
  });

  const lastRowsRef = React.useRef(tableRows);
  const lastColumnsRef = React.useRef(tableColumns);

  const queryClient = useQueryClient();
  const gridApiRef = useGridApiRef();

  const onViewRowDetails = React.useCallback(
    (record: GridValidRowModel) => {
      setDetailIds([record.row[CLOUD_ASSET_UNIQUE_KEY]]);
      queryClient.setQueryData(
        getPlatformTableRowByIdTeamQueryKey(
          teamName,
          CLOUD_ASSET_TABLE_NAME,
          record.row[CLOUD_ASSET_UNIQUE_KEY],
        ),
        { data: { data: record.row } },
      );
    },
    [queryClient, setDetailIds, teamName],
  );
  const { cellModesModel, handleCellClick, handleCellModesModelChange, handleMouseDown } =
    useDataGridClick({
      onClick: onViewRowDetails,
    });

  const rows = useMemo(() => {
    if (JSON.stringify(tableRows) !== JSON.stringify(lastRowsRef.current)) {
      lastRowsRef.current = tableRows;
    }

    return lastRowsRef.current;
  }, [tableRows]);

  const columns = useMemo(() => {
    if (JSON.stringify(tableColumns) !== JSON.stringify(lastColumnsRef.current)) {
      lastColumnsRef.current = tableColumns;
    }

    return lastColumnsRef.current;
  }, [tableColumns]);

  const columnVisibilityModel: GridColumnVisibilityModel = useMemo(() => {
    const model: GridColumnVisibilityModel = {};
    for (const col of columns) {
      model[col.field] = displayColumns.has(col.field);
    }

    return model;
  }, [columns, displayColumns]);

  useEffect(() => {
    if (gridApiRef.current?.autosizeColumns) {
      resizeTimer.current = window.setTimeout(() => {
        // Filter out the checkbox column from being autosized
        const columnsToAutosize =
          gridApiRef.current?.getAllColumns().filter((col) => col.field !== '__check__') || [];

        gridApiRef.current?.autosizeColumns({
          columns: columnsToAutosize.map((col) => col.field),
          expand: true,
          includeHeaders: true,
          includeOutliers: true,
        });
        setLastRows(rows);
      }, 100);
    }

    return () => {
      if (resizeTimer.current) {
        clearTimeout(resizeTimer.current);
      }
    };
  }, [rows, gridApiRef]); // run autosize if rows change

  const isLoading = useMemo(() => dataLoading || rows !== lastRows, [dataLoading, rows, lastRows]);

  const hasColumns = useMemo(() => {
    return Object.entries(columnVisibilityModel).some(([_, visible]) => visible);
  }, [columnVisibilityModel]);

  const tooltipColumns = useMemo(() => {
    return columns.map((col) => {
      // If column is editable or already has a custom renderCell, don't override it
      if (col.editable || col.renderCell) {
        return col;
      }

      return {
        ...col,
        renderCell: (params: GridRenderCellParams<any, any>) => {
          const displayValue = params.value == null ? '' : String(params.value);

          return (
            <Tooltip placement="top" title={displayValue}>
              <span>{displayValue}</span>
            </Tooltip>
          );
        },
      };
    });
  }, [columns]);

  const slots = React.useMemo(
    () => ({
      noResultsOverlay: () => {
        if (!hasColumns) {
          return (
            <EmptyBox
              action={
                <Button
                  onClick={() =>
                    overwriteDisplayColumns(new Set(Object.keys(columnVisibilityModel)))
                  }
                  variant="contained"
                >
                  Reset columns
                </Button>
              }
              sx={{ height: '100%', paddingTop: '25%' }}
              title="No columns selected"
            />
          );
        }

        return (
          <Stack alignItems="center" height="100%" justifyContent="center">
            No results found.
          </Stack>
        );
      },
      noRowsOverlay: () => {
        if (error?.status === 404) {
          return <NotInitialized href="/" />;
        }

        if (filter.length > 0) {
          return (
            <EmptyBox
              action={
                <Button onClick={() => overwriteFilter('')} variant="contained">
                  Clear all filters
                </Button>
              }
              sx={{ height: '100%', paddingTop: '25%' }}
              title="No results found"
            />
          );
        }

        return <GridNoRowsOverlay />;
      },
    }),
    [
      error?.status,
      filter,
      columnVisibilityModel,
      hasColumns,
      overwriteDisplayColumns,
      overwriteFilter,
    ],
  );

  const processRowUpdate = async (newRow: GridValidRowModel, oldRow: GridValidRowModel) => {
    const editedColumnName = Object.keys(newRow).find((key) => newRow[key] !== oldRow[key]);
    const editedColumn = columns.find((col) => col.field === editedColumnName)!;

    if (editedColumn) {
      const success = await editCustomColumnValues({
        customColumnId: editedColumn.id!,
        rowIds: [newRow[ROW_EDIT_ID_FIELD]],
        successMessage: `New ${editedColumn!.headerName} value has been saved`,
        teamName,
        value: newRow[editedColumnName!],
      });

      return success ? newRow : oldRow;
    }

    return newRow;
  };

  return (
    <Stack height="100%" p={3} width="100%">
      <Box width="100%">
        <CustomToolbar
          enableWriteActions={enableWriteActions}
          gridApiRef={gridApiRef}
          onSaveCurrentFilter={onSaveCurrentFilter}
          paginationModel={paginationModel}
          rowCount={rowCount}
          teamName={teamName}
        />
      </Box>
      <Box height="100%" minHeight={0} onMouseDown={handleMouseDown} width="100%">
        <DataGrid
          apiRef={gridApiRef}
          columnHeaderHeight={theme.sizing.tableRowHeight}
          columns={tooltipColumns}
          columnVisibilityModel={columnVisibilityModel}
          disableColumnMenu={true}
          getRowId={(row: GridValidRowModel) => row[ROW_EDIT_ID_FIELD]}
          hideFooterSelectedRowCount={true}
          initialState={{
            pagination: { paginationModel: { pageSize: 25 } },
          }}
          loading={isLoading}
          localeText={{
            paginationDisplayedRows: () => ``,
          }}
          onCellClick={handleCellClick}
          onPaginationModelChange={setPagination}
          onSortModelChange={setSort}
          paginationMode="server"
          paginationModel={paginationModel}
          rowCount={rowCount}
          rowHeight={theme.sizing.tableRowHeight}
          rows={hasColumns ? rows : []}
          slotProps={slotProps}
          slots={slots}
          sortingMode="server"
          sortModel={sort}
          sx={{
            [`& .${gridClasses.cell}:focus`]: {
              outline: 'none',
            },
            [`& .${gridClasses.cell}:focus-within`]: {
              outline: 'none',
            },
            [`& .${gridClasses.row}:hover`]: {
              cursor: 'pointer',
            },
            border: 'none',
            borderRadius: 1,
          }}
          {...(allowEditingCustomColumns && hasEditableCustomColumns
            ? {
                cellModesModel,
                checkboxSelection: true,
                onCellModesModelChange: handleCellModesModelChange,
                onRowSelectionModelChange: setSelectedRows,
                processRowUpdate,
              }
            : {})}
        />
        <CustomColumnsMultiEdit
          clearSelectedRows={() =>
            gridApiRef.current?.setRowSelectionModel({
              ids: new Set(),
              type: 'include',
            })
          }
          columns={columns}
          selectedRows={selectedRows}
          teamName={teamName}
        />
      </Box>
      {detailIds?.map((id, index) => (
        <AssetInventoryDetailView key={`${id}-${index}`} recordId={id} />
      ))}
    </Stack>
  );
}
